import type { HttpTypes } from "@medusajs/types";

import type { FetchError } from "@repo/api-sdk";
import sdk from "@repo/api-sdk";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { addressesQueryKeys } from "./queryKeys";

/**
 * Query options for fetching customer addresses
 */
export function addressesQuery(
  query: HttpTypes.SelectParams = {
    fields: "+address_name"
  }
) {
  return queryOptions({
    queryKey: addressesQueryKeys.list(query),
    queryFn: async () => {
      const data = await sdk.store.customer.listAddress(query);
      return data;
    },
    retry: false,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
}

/**
 * Hook for fetching customer addresses
 */
export function useAddresses(options?: { enabled?: boolean }) {
  return useQuery({
    ...addressesQuery(),
    ...options
  });
}

/**
 * Address data for create/update operations
 */
export type AddressInput =
  | HttpTypes.StoreCreateCustomerAddress
  | HttpTypes.StoreUpdateCustomerAddress;

/**
 * Hook for creating a new customer address
 */
export function useCreateAddress() {
  const queryClient = useQueryClient();

  return useMutation<
    HttpTypes.StoreCustomerResponse,
    FetchError,
    HttpTypes.StoreCreateCustomerAddress
  >({
    mutationFn: async (address) => {
      return await sdk.store.customer.createAddress(address);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: addressesQueryKeys.all });
    }
  });
}

/**
 * Hook for updating an existing customer address
 */
export function useUpdateAddress() {
  const queryClient = useQueryClient();

  return useMutation<
    HttpTypes.StoreCustomerResponse,
    FetchError,
    { addressId: string; address: HttpTypes.StoreUpdateCustomerAddress }
  >({
    mutationFn: async ({ addressId, address }) => {
      return await sdk.store.customer.updateAddress(addressId, address);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: addressesQueryKeys.all });
    }
  });
}

/**
 * Hook for deleting a customer address
 */
export function useDeleteAddress() {
  const queryClient = useQueryClient();

  return useMutation<
    HttpTypes.StoreCustomerAddressDeleteResponse,
    FetchError,
    string
  >({
    mutationFn: async (addressId) => {
      return await sdk.store.customer.deleteAddress(addressId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: addressesQueryKeys.all });
    }
  });
}
